import React from "react";
import { Store, MapPin, Loader2 } from "lucide-react";
import { REWARDS } from "@/constants/testIds";

/**
 * Horizontal chip picker for the current partner-store context. The
 * selected store scopes every follow-up card (review-proof,
 * redeem) so the customer picks once and the rest of the flow flows.
 * Iter-352 — contextual "Sort by nearest" button + distance labels
 * when the backend serves distance_km.
 */
export default function StorePickerChips({ stores = [], selectedId, onSelect, onSortNearest, geoState = "idle" }) {
  if (!stores.length) {
    return (
      <div
        data-testid={REWARDS.storesChips}
        className="rounded-2xl bg-white border border-soft p-6 text-center text-sm text-slate-500"
      >
        No partner stores yet — check back soon!
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-1.5">
        <Store className="w-3.5 h-3.5" /> Partner stores
        {onSortNearest && (
          <span className="ml-auto normal-case tracking-normal">
            {geoState === "active" ? (
              <span data-testid="rewards-nearest-active" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                <MapPin className="w-3 h-3" /> Nearest first
              </span>
            ) : geoState === "denied" ? (
              <span data-testid="rewards-nearest-denied" className="text-[11px] font-medium text-slate-400">
                Location unavailable — showing A–Z
              </span>
            ) : (
              <button
                type="button"
                onClick={onSortNearest}
                disabled={geoState === "locating"}
                data-testid="rewards-sort-nearest-btn"
                className="inline-flex items-center gap-1 rounded-full border border-soft bg-white px-3 py-1 text-[11px] font-semibold text-slate-600 hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)] transition-colors disabled:opacity-60"
              >
                {geoState === "locating" ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" /> Locating…
                  </>
                ) : (
                  <>
                    <MapPin className="w-3 h-3" /> Sort by nearest
                  </>
                )}
              </button>
            )}
          </span>
        )}
      </div>
      <div
        data-testid={REWARDS.storesChips}
        className="flex gap-2 overflow-x-auto no-scrollbar pb-1"
      >
        {stores.map((s) => {
          const active = selectedId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              data-testid={REWARDS.storeChip(s.id)}
              onClick={() => onSelect(s.id)}
              aria-pressed={active}
              className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors whitespace-nowrap ${
                active
                  ? "bg-[color:var(--brand-blue)] text-white border-transparent"
                  : "bg-white text-slate-700 border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
              }`}
            >
              {s.name}
              {s.distance_km != null && (
                <span className={active ? "text-white/80" : "text-slate-400"}> · {s.distance_km} km</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
